import { Component, effect, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { AppErrorService } from '../../../core/services/app-error.service';
import { ErrorBannerService } from '../../../core/services/error-banner.service';
import { PortalService } from '../../../core/services/portal.service';
import { SeoService } from '../../../core/services/seo.service';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private appErrorService: AppErrorService,
    private errorBannerService: ErrorBannerService,
    private portalService: PortalService,
    private seoService: SeoService
  ) {
    this.updatePageSeo();

    effect(() => {
      const portal = this.portalService.portal();
      const portalName = String(portal?.name ?? portal?.portal_name ?? '').trim();
      this.updatePageSeo(portalName);
    });
  }

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  private updatePageSeo(portalName = ''): void {
    const pageTitle = portalName ? `${portalName} Login` : 'Login';
    this.seoService.setPageSeo({
      title: pageTitle,
      ogTitle: pageTitle
    });
  }

  handleLogin(): void {
    this.errorBannerService.clear();

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    const { email, password } = this.loginForm.value;
    this.authService
      .login({ email, password })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: any) => {
          const token =
            res?.access_token ??
            res?.token ??
            res?.data?.access_token ??
            res?.data?.token;

          if (!token) {
            this.errorBannerService.show('Login succeeded but no access token was returned.');
            return;
          }

          this.authService.setToken(token);

          const role = res?.user_role ?? res?.role ?? res?.data?.user_role ?? res?.data?.role;
          if (role) {
            this.authService.setRole(role);
          }

          const displayName =
            res?.name ??
            res?.full_name ??
            res?.user?.name ??
            res?.user?.full_name ??
            res?.data?.name ??
            res?.data?.full_name ??
            email;
          this.authService.setDisplayName(displayName);

          this.router.navigate(['/certification']);
        },
        error: (err) => {
          const message = this.appErrorService.resolveInlineMessage(err);
          if (message) {
            this.errorBannerService.show(message);
          }
        },
      });
  }
}
